import { FRIEND_DAILY_WIN_COPY, GAME_NAME, TYPE } from "@gaoya/shared";
import { IDIOM_ANECDOTES } from "../anecdotes.js";
import { C, capsuleBox, clipName, drawArt, drawCircleAvatar, drawPanel, drawSeal, fillRound, pageHeader, wrapLines } from "../theme.js";
import { drawCupRow } from "../widgets/cups.js";
import type { UiState } from "../../game/state.js";
import { layoutMatching } from "../../ui/hall.js";

export function drawMatching(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number, now: number): void {
  pageHeader(ctx, ui, w, GAME_NAME, ui.mode === "RUSH" ? "抢答匹配" : "1 对 1 匹配");
  const cap = capsuleBox(ui, w);
  const wait = Math.max(0, Math.floor((now - ui.matchingSince) / 1000));
  const mm = String(Math.floor(wait / 60)).padStart(2, "0");
  const ss = String(wait % 60).padStart(2, "0");
  const cancelY = layoutMatching(w, h, ui)[0]?.y ?? h * 0.86;
  const compact = h < 720;
  const y0 = cap.bottom + (compact ? 16 : 28);

  // 状态
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const pulse = 0.5 + 0.5 * Math.sin(now / 260);
  ctx.font = `700 12px ${TYPE.sans}`;
  const pill = "正在寻访对手";
  const pw = ctx.measureText(pill).width + 34;
  fillRound(ctx, (w - pw) / 2, y0, pw, 24, 12, C.sealSoft);
  ctx.fillStyle = `rgba(184,58,36,${0.45 + pulse * 0.55})`;
  ctx.beginPath();
  ctx.arc((w - pw) / 2 + 13, y0 + 12, 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = C.seal;
  ctx.fillText(pill, w / 2 + 7, y0 + 12.5);

  ctx.fillStyle = C.ink;
  ctx.font = `700 ${compact ? 36 : 44}px ${TYPE.serif}`;
  ctx.fillText(`${mm}:${ss}`, w / 2, y0 + (compact ? 52 : 62));
  ctx.fillStyle = C.ink2;
  ctx.font = `13px ${TYPE.sans}`;
  ctx.fillText("掌柜正在张罗对席茶客 · 有人应战即开局", w / 2, y0 + (compact ? 84 : 100));

  // 对席
  const cardY = y0 + (compact ? 104 : 128);
  const gap = 12;
  const cardW = (w - 32 - gap) / 2;
  const cardH = compact ? 168 : 196;
  drawSeatCard(ctx, { x: 16, y: cardY, w: cardW, h: cardH }, ui.avatarImg, ui.nickname, true, now);
  drawSeatCard(ctx, { x: 16 + cardW + gap, y: cardY, w: cardW, h: cardH }, null, "寻访茶客中", false, now);
  drawSeal(ctx, w / 2, cardY + cardH / 2, 42, "战", C.seal, "#FFF7EE", -0.08);

  const bannerH = 40;
  const showFriendHint = ui.mode !== "RUSH";
  const bannerY = cardY + cardH + 16;
  const canBanner = showFriendHint && cancelY - bannerY >= bannerH + 8;
  if (canBanner) drawFriendWinHint(ctx, 16, bannerY, w - 32, bannerH);

  // 茶余小憩
  const n = IDIOM_ANECDOTES.length;
  const start = Math.abs(ui.matchingSince) % n;
  const note = IDIOM_ANECDOTES[(start + Math.floor((now - ui.matchingSince) / 8000)) % n] ?? IDIOM_ANECDOTES[0]!;
  const ny = canBanner ? bannerY + bannerH + 12 : cardY + cardH + 16;
  const nh = Math.min(140, cancelY - 16 - ny);
  if (nh < 70) return;
  drawPanel(ctx, 16, ny, w - 32, nh, 16, C.cardSoft);
  fillRound(ctx, 16, ny + 16, 3, 20, 1.5, C.gold);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.goldDeep;
  ctx.font = `700 12px ${TYPE.sans}`;
  ctx.fillText("茶余小憩 · 成语雅故", 30, ny + 26);
  ctx.fillStyle = C.ink;
  ctx.font = `700 18px ${TYPE.serif}`;
  ctx.fillText(note[0], 30, ny + 52);
  ctx.fillStyle = C.ink2;
  ctx.font = `13px ${TYPE.sans}`;
  ctx.textBaseline = "top";
  const lines = wrapLines(ctx, note[1], w - 64);
  const maxLines = Math.max(1, Math.floor((nh - 72) / 20));
  for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
    let line = lines[i] ?? "";
    if (i === maxLines - 1 && lines.length > maxLines) line = clipName(ctx, line + "…", w - 64);
    ctx.fillText(line, 30, ny + 68 + i * 20);
  }
}

function drawFriendWinHint(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  drawPanel(ctx, x, y, w, h, 14, C.goldSoft);
  ctx.fillStyle = C.goldDeep;
  ctx.font = `600 13px ${TYPE.sans}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(clipName(ctx, FRIEND_DAILY_WIN_COPY, w - 24), x + w / 2, y + h / 2 + 0.5);
}

function drawSeatCard(
  ctx: CanvasRenderingContext2D,
  r: { x: number; y: number; w: number; h: number },
  img: CanvasImageSource | null,
  name: string,
  mine: boolean,
  now: number,
): void {
  drawPanel(ctx, r.x, r.y, r.w, r.h, 16, mine ? C.card : C.cardSoft);
  const cx = r.x + r.w / 2;
  const avY = r.y + r.h * 0.3;
  if (mine) {
    drawCircleAvatar(ctx, img, cx, avY, 30, name);
  } else {
    const t = (now % 1800) / 1800;
    for (let k = 0; k < 2; k++) {
      const p = (t + k * 0.5) % 1;
      ctx.strokeStyle = `rgba(184,58,36,${0.35 * (1 - p)})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, avY, 30 + p * 16, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, avY, 30, 0, Math.PI * 2);
    ctx.fillStyle = C.well;
    ctx.fill();
    ctx.clip();
    drawArt(ctx, "char-scholar-cut", cx - 32, avY - 26, 64, 58, true);
    ctx.fillStyle = "rgba(241,232,216,0.45)";
    ctx.fillRect(cx - 32, avY - 32, 64, 64);
    ctx.restore();
  }
  ctx.fillStyle = mine ? C.ink : C.ink2;
  ctx.font = `700 15px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(clipName(ctx, name, r.w - 20), cx, r.y + r.h * 0.3 + 50);
  ctx.fillStyle = C.ink3;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(mine ? "三盏茶命" : "虚席以待", cx, r.y + r.h * 0.3 + 70);
  drawCupRow(ctx, { x: r.x + 12, y: r.y + r.h - 46, w: r.w - 24, h: 36 }, mine ? 3 : 0, now, 0.72);
}
